import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { nzTodayIso as nzToday } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ROOT = "/platform/finance";
const money = (cents) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    (cents || 0) / 100,
  );
const fieldClass =
  "w-full rounded-sm border border-zinc-400 bg-transparent p-2 text-sm";
const emptyLine = () => ({
  description: "Fieldlink software subscription",
  quantity: "1",
  unit_price: "",
  tax: "standard",
});
const emptyInvoice = () => ({
  request_id: crypto.randomUUID(),
  customer_name: "",
  customer_email: "",
  customer_address: "",
  org_id: "",
  issue_date: nzToday(),
  due_date: nzToday(),
  reference: "",
  notes: "",
  lines: [emptyLine()],
});
const emptyExpense = () => ({
  request_id: crypto.randomUUID(),
  supplier: "",
  supplier_gst_number: "",
  reference: "",
  description: "",
  category: "Software",
  date: nzToday(),
  total: "",
  gst: "0",
  business_percent: "100",
  paid_date: "",
  evidence_held: false,
});
function errorMessage(e) {
  const detail = e.response?.data?.detail;
  return Array.isArray(detail)
    ? detail.map((d) => d.msg).join("; ")
    : detail || e.message || "Unable to complete action";
}
function Field({ label, children }) {
  return (
    <label className="block text-sm font-medium space-y-1">
      <span>{label}</span>
      {children}
    </label>
  );
}
function TextField({ label, name, value, onChange, ...props }) {
  return (
    <Field label={label}>
      <Input
        name={name}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        {...props}
      />
    </Field>
  );
}
function Pages({ data, onPage }) {
  return data && data.total > 50 ? (
    <div className="flex items-center gap-3">
      <Button
        variant="outline"
        disabled={data.page <= 1}
        onClick={() => onPage(data.page - 1)}
      >
        Previous
      </Button>
      <span>
        Page {data.page} of {Math.ceil(data.total / 50)}
      </span>
      <Button
        variant="outline"
        disabled={data.page * 50 >= data.total}
        onClick={() => onPage(data.page + 1)}
      >
        Next
      </Button>
    </div>
  ) : null;
}
async function download(path, filename, params) {
  const { data } = await api.get(ROOT + path, { responseType: "blob", params });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function PlatformFinance() {
  const [section, setSection] = useState("invoices");
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [emailReady, setEmailReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const loadSettings = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get(ROOT + "/settings");
      setConfig(data.settings);
      setEmailReady(data.email_configured);
    } catch (e) {
      setLoadError(errorMessage(e));
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      setRevision((v) => v + 1);
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (!loaded) return <p role="status">Loading finance…</p>;
  if (loadError)
    return (
      <div role="alert">
        {loadError} <Button onClick={loadSettings}>Retry</Button>
      </div>
    );
  return (
    <div className="space-y-5" data-testid="platform-finance">
      <div className="hard-card p-5">
        <h2 className="text-xl font-bold">Software business finances</h2>
        <p className="text-sm text-zinc-500 mt-1">
          NZD invoices, expense records and GST worksheets for your accountant.
        </p>
      </div>
      {!config && (
        <div className="p-4 border border-amber-400 bg-amber-50 text-amber-950">
          Start with Business settings: enter your legal business name, GST
          number, payment details and registered GST accounting basis.{" "}
          <Button variant="outline" onClick={() => setSection("settings")}>
            Set up business
          </Button>
        </div>
      )}
      <nav aria-label="Finance sections" className="flex flex-wrap gap-2">
        {[
          ["invoices", "Invoices"],
          ["expenses", "Expenses"],
          ["reports", "GST & accountant exports"],
          ["settings", "Business settings"],
        ].map(([key, label]) => (
          <Button
            key={key}
            variant={section === key ? "default" : "outline"}
            aria-pressed={section === key}
            onClick={() => setSection(key)}
          >
            {label}
          </Button>
        ))}
      </nav>
      {section === "settings" && (
        <Settings
          config={config}
          busy={busy}
          run={run}
          onSaved={loadSettings}
        />
      )}
      {section === "invoices" && (
        <Invoices
          configured={!!config}
          emailReady={emailReady}
          busy={busy}
          run={run}
          revision={revision}
        />
      )}
      {section === "expenses" && (
        <Expenses busy={busy} run={run} revision={revision} />
      )}
      {section === "reports" && (
        <Reports config={config} busy={busy} run={run} />
      )}
    </div>
  );
}

function Settings({ config, busy, run, onSaved }) {
  const [form, setForm] = useState(
    config || {
      legal_name: "",
      gst_number: "",
      address: "",
      email: "",
      bank_account: "",
      gst_basis: "",
    },
  );
  const change = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  return (
    <form
      className="hard-card p-5 space-y-4 max-w-3xl"
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          await api.put(ROOT + "/settings", form);
          await onSaved();
          toast.success("Business settings saved");
        });
      }}
    >
      <h3 className="text-lg font-bold">GST-registered business</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <TextField
          label="Legal business name"
          value={form.legal_name}
          onChange={change("legal_name")}
          required
          maxLength={200}
        />
        <TextField
          label="GST number"
          value={form.gst_number}
          onChange={change("gst_number")}
          placeholder="123-456-789"
          required
        />
        <TextField
          label="Business address"
          value={form.address}
          onChange={change("address")}
          required
          maxLength={500}
        />
        <TextField
          label="Accounts email / reply-to"
          type="email"
          value={form.email}
          onChange={change("email")}
          required
        />
        <TextField
          label="Bank account for customer payments"
          value={form.bank_account}
          onChange={change("bank_account")}
          required
        />
        <Field label="Registered GST accounting basis">
          <select
            className={fieldClass}
            value={form.gst_basis}
            onChange={(e) => change("gst_basis")(e.target.value)}
            required
          >
            <option value="">Choose your registered basis</option>
            <option value="payments">Payments</option>
            <option value="invoice">Invoice</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </Field>
      </div>
      <p className="text-sm text-zinc-500">
        Use the basis shown in your myIR GST registration. Issued invoices
        retain their original business details.
      </p>
      <Button disabled={busy} type="submit">
        {busy ? "Saving…" : "Save business settings"}
      </Button>
    </form>
  );
}

function Invoices({ configured, emailReady, busy, run, revision }) {
  const [page, setPage] = useState(1),
    [data, setData] = useState(null),
    [error, setError] = useState("");
  const [form, setForm] = useState(null),
    [editing, setEditing] = useState(null),
    [payment, setPayment] = useState(null);
  const [search, setSearch] = useState(""),
    [orgs, setOrgs] = useState([]);
  useEffect(() => {
    let active = true;
    setError("");
    api
      .get(ROOT + "/invoices", { params: { page } })
      .then((r) => {
        if (active) setData(r.data);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [page, revision]);
  useEffect(() => {
    if (!form) return;
    const controller = new AbortController();
    const timer = setTimeout(
      () =>
        api
          .get("/platform/crm/organisations", {
            params: { q: search },
            signal: controller.signal,
          })
          .then((r) => setOrgs(r.data.items || []))
          .catch((e) => {
            if (e.code !== "ERR_CANCELED")
              toast.error("Could not load organisations");
          }),
      250,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search, !!form]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const act = (id, action, body, message) =>
    run(async () => {
      await api.post(`${ROOT}/invoices/${id}/${action}`, body);
      toast.success(message);
    });
  const edit = (inv) => {
    setEditing(inv.id);
    setForm(
      Object.fromEntries(
        Object.keys(emptyInvoice()).map((k) => [
          k,
          k === "lines"
            ? inv.lines.map(({ description, quantity, unit_price, tax }) => ({
                description,
                quantity,
                unit_price,
                tax,
              }))
            : inv[k] || "",
        ]),
      ),
    );
  };
  return (
    <div className="space-y-4">
      {!emailReady && (
        <p className="text-sm text-amber-700">
          Email delivery is not configured. You can issue invoices and download
          PDFs to send manually.
        </p>
      )}
      <p className="text-sm text-zinc-500">
        Use this register for manually billed software customers. Stripe
        subscription invoices and legacy billing records are outside this
        register.
      </p>
      <Button
        disabled={!configured || busy}
        onClick={() => {
          setEditing(null);
          setForm(emptyInvoice());
        }}
      >
        New invoice
      </Button>
      {form && (
        <form
          className="hard-card p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              if (editing) await api.put(`${ROOT}/invoices/${editing}`, form);
              else await api.post(ROOT + "/invoices", form);
              setForm(null);
              toast.success("Draft saved");
            });
          }}
        >
          <h3 className="font-bold text-lg">
            {editing ? "Edit draft" : "New draft invoice"}
          </h3>
          {!editing && (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField
                label="Find an existing customer"
                value={search}
                onChange={setSearch}
                placeholder="Search organisations…"
              />
              <Field label="Link customer organisation (optional)">
                <select
                  className={fieldClass}
                  value={form.org_id}
                  onChange={(e) => {
                    const org = orgs.find((o) => o.id === e.target.value);
                    setForm((f) => ({
                      ...f,
                      org_id: org?.id || "",
                      customer_name: org?.name || f.customer_name,
                      customer_email:
                        org?.contact_email ||
                        org?.owner_email ||
                        f.customer_email,
                    }));
                  }}
                >
                  <option value="">Enter customer details manually</option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField
              label="Customer name"
              value={form.customer_name}
              onChange={change("customer_name")}
              required
            />
            <TextField
              label="Customer email"
              value={form.customer_email}
              onChange={change("customer_email")}
              type="email"
              required
            />
            <TextField
              label="Customer address"
              value={form.customer_address}
              onChange={change("customer_address")}
            />
            <TextField
              label="Reference / service period"
              value={form.reference}
              onChange={change("reference")}
              placeholder="e.g. October 2026 subscription"
            />
            <TextField
              label="Issue date"
              type="date"
              value={form.issue_date}
              onChange={change("issue_date")}
              max={nzToday()}
              required
            />
            <TextField
              label="Due date"
              type="date"
              value={form.due_date}
              onChange={change("due_date")}
              min={form.issue_date}
              required
            />
          </div>
          {form.lines.map((line, i) => (
            <div key={i} className="grid sm:grid-cols-5 gap-3 border-t pt-3">
              {[
                ["description", "Description", "text"],
                ["quantity", "Quantity", "number"],
                ["unit_price", "Unit price excl. GST", "number"],
              ].map(([key, label, type]) => (
                <TextField
                  key={key}
                  label={label}
                  type={type}
                  value={line[key]}
                  required
                  min={key === "quantity" ? "0.0001" : "0"}
                  step={key === "quantity" ? "0.0001" : "0.01"}
                  onChange={(value) =>
                    setForm((f) => ({
                      ...f,
                      lines: f.lines.map((row, j) =>
                        j === i ? { ...row, [key]: value } : row,
                      ),
                    }))
                  }
                />
              ))}
              <Field label="GST treatment">
                <select
                  className={fieldClass}
                  value={line.tax}
                  onChange={(e) => {
                    const tax = e.target.value;
                    setForm((f) => ({
                      ...f,
                      lines: f.lines.map((row, j) =>
                        j === i ? { ...row, tax } : row,
                      ),
                    }));
                  }}
                >
                  <option value="standard">15% GST</option>
                  <option value="zero">Zero-rated</option>
                  <option value="exempt">Exempt</option>
                </select>
              </Field>
              <Button
                type="button"
                variant="outline"
                disabled={form.lines.length === 1}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    lines: f.lines.filter((_, j) => j !== i),
                  }))
                }
              >
                Remove line {i + 1}
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            disabled={form.lines.length >= 100}
            onClick={() =>
              setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))
            }
          >
            Add line
          </Button>
          <TextField
            label="Notes / payment terms"
            value={form.notes}
            onChange={change("notes")}
          />
          <p className="text-sm text-zinc-500">
            Save a draft to review exact totals and the PDF before issuing.
            Issuing makes the invoice read-only and includes it in invoice-basis
            reports.
          </p>
          <div className="flex gap-2">
            <Button disabled={busy} type="submit">
              Save draft
            </Button>
            <Button
              disabled={busy}
              type="button"
              variant="outline"
              onClick={() => setForm(null)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}
      {!data && !error && <p role="status">Loading invoices…</p>}
      {data?.items.length === 0 && (
        <div className="hard-card p-8 text-center">
          No invoices yet. Create your first software invoice above.
        </div>
      )}
      {data?.items.map((inv) => (
        <article key={inv.id} className="hard-card p-4 space-y-3">
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <h3 className="font-bold">
                {inv.number} · {inv.customer_name}
              </h3>
              <p className="text-sm text-zinc-500">
                {inv.customer_email} · Due {inv.due_date} ·{" "}
                {inv.display_status.replaceAll("_", " ")}
              </p>
            </div>
            <div className="text-right">
              <strong>{money(inv.total_cents)}</strong>
              <p className="text-sm">
                GST {money(inv.gst_cents)} · Balance {money(inv.balance_cents)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {inv.status !== "void" && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run(() =>
                    download(`/invoices/${inv.id}/pdf`, inv.number + ".pdf"),
                  )
                }
              >
                Download {inv.status === "draft" ? "draft " : ""}PDF
              </Button>
            )}
            {inv.status === "draft" && (
              <>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => edit(inv)}
                >
                  Edit draft
                </Button>
                <Button
                  disabled={busy}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Issue ${inv.number} for ${money(inv.total_cents)}? It will become read-only and enter invoice-basis reports.`,
                      )
                    )
                      act(inv.id, "issue", {}, "Invoice issued");
                  }}
                >
                  Issue invoice
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    const reason = window.prompt(
                      "Reason for cancelling this draft:",
                    );
                    if (reason)
                      act(inv.id, "void", { reason }, "Draft cancelled");
                  }}
                >
                  Cancel draft
                </Button>
              </>
            )}
            {inv.status === "issued" && (
              <>
                <Button
                  disabled={
                    busy || !emailReady || inv.email_status !== "not_sent"
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        `Email ${inv.number} to ${inv.customer_email}?`,
                      )
                    )
                      act(inv.id, "send", {}, "Email accepted by provider");
                  }}
                >
                  {inv.email_status === "sent"
                    ? "Email accepted"
                    : inv.email_status === "not_sent"
                      ? "Email invoice"
                      : "Email needs checking"}
                </Button>
                {inv.balance_cents > 0 && (
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => setPayment(inv)}
                  >
                    Record payment
                  </Button>
                )}
              </>
            )}
          </div>
          {inv.payments.length > 0 && (
            <details>
              <summary className="text-sm cursor-pointer">
                Payment history ({inv.payments.length})
              </summary>
              {inv.payments.map((p) => (
                <p className="text-sm mt-1" key={p.id}>
                  {p.date} · {money(p.amount_cents)} ·{" "}
                  {p.reference || "No reference"} ·{" "}
                  {p.reversed_at ? (
                    "Reversed entry"
                  ) : (
                    <button
                      type="button"
                      className="underline"
                      disabled={busy}
                      onClick={() => {
                        const reason = window.prompt(
                          "Reason for correcting this mistaken payment record? This is not a refund. Regenerate affected GST worksheets and notify your accountant if already filed.",
                        );
                        if (reason)
                          act(
                            inv.id,
                            `payments/${p.id}/reverse`,
                            { reason },
                            "Payment record reversed; regenerate affected worksheets",
                          );
                      }}
                    >
                      Correct mistaken payment
                    </button>
                  )}
                </p>
              ))}
            </details>
          )}
        </article>
      ))}
      <Pages data={data} onPage={setPage} />
      {payment && (
        <PaymentForm
          key={payment.id}
          title={`Payment for ${payment.number}`}
          total={payment.balance_cents}
          busy={busy}
          onClose={() => setPayment(null)}
          onSave={(body) =>
            run(async () => {
              await api.post(`${ROOT}/invoices/${payment.id}/payments`, body);
              setPayment(null);
              toast.success("Payment recorded");
            })
          }
        />
      )}
    </div>
  );
}

function PaymentForm({
  title,
  total,
  busy,
  onClose,
  onSave,
  fullOnly = false,
}) {
  const [form, setForm] = useState({
    request_id: crypto.randomUUID(),
    amount: (total / 100).toFixed(2),
    date: nzToday(),
    reference: "",
  });
  return (
    <form
      className="hard-card border-2 border-orange-500 p-5 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <h3 className="font-bold">{title}</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <TextField
          label="Amount NZD"
          type="number"
          min="0.01"
          max={total / 100}
          step="0.01"
          required
          readOnly={fullOnly}
          value={form.amount}
          onChange={(amount) => setForm((f) => ({ ...f, amount }))}
        />
        <TextField
          label="Payment date"
          type="date"
          required
          max={nzToday()}
          value={form.date}
          onChange={(date) => setForm((f) => ({ ...f, date }))}
        />
        <TextField
          label="Bank reference"
          value={form.reference}
          onChange={(reference) => setForm((f) => ({ ...f, reference }))}
        />
      </div>
      <div className="flex gap-2">
        <Button disabled={busy}>Save payment</Button>
        <Button
          disabled={busy}
          type="button"
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Expenses({ busy, run, revision }) {
  const [page, setPage] = useState(1),
    [data, setData] = useState(null),
    [error, setError] = useState("");
  const [form, setForm] = useState(null),
    [payment, setPayment] = useState(null),
    [editing, setEditing] = useState(null);
  useEffect(() => {
    let active = true;
    setError("");
    api
      .get(ROOT + "/expenses", { params: { page } })
      .then((r) => {
        if (active) setData(r.data);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [page, revision]);
  const change = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  return (
    <div className="space-y-4">
      <Button
        disabled={busy}
        onClick={() => {
          setEditing(null);
          setForm(emptyExpense());
        }}
      >
        Record expense
      </Button>
      {form && (
        <form
          className="hard-card p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              const body = { ...form, paid_date: form.paid_date || null };
              if (editing)
                await api.put(`${ROOT}/expenses/${editing.id}`, body, {
                  params: { version: editing.version },
                });
              else await api.post(ROOT + "/expenses", body);
              setForm(null);
              toast.success(
                editing
                  ? "Expense corrected; regenerate affected worksheets"
                  : "Expense recorded; attach its receipt below",
              );
            });
          }}
        >
          <h3 className="text-lg font-bold">
            {editing ? "Correct expense" : "Business expense"}
          </h3>
          {editing && (
            <p className="text-sm text-amber-700">
              Corrections affect earlier GST worksheets. Regenerate affected
              exports and notify your accountant if already filed.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField
              label="Supplier"
              value={form.supplier}
              onChange={change("supplier")}
              required
            />
            <TextField
              label="Supplier GST number (if shown)"
              value={form.supplier_gst_number}
              onChange={change("supplier_gst_number")}
            />
            <TextField
              label="Supplier invoice / receipt reference"
              value={form.reference}
              onChange={change("reference")}
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={change("description")}
              required
            />
            <Field label="Category">
              <select
                className={fieldClass}
                value={form.category}
                onChange={(e) => change("category")(e.target.value)}
              >
                {[
                  "Hosting",
                  "Software",
                  "Marketing",
                  "Professional fees",
                  "Office",
                  "Travel",
                  "Bank fees",
                  "Other",
                ].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
            <TextField
              label="Supplier invoice received date"
              type="date"
              max={nzToday()}
              value={form.date}
              onChange={change("date")}
              required
            />
            <TextField
              label="Total paid / payable incl. GST (NZD)"
              type="number"
              min="0.01"
              step="0.01"
              value={form.total}
              onChange={change("total")}
              required
            />
            <TextField
              label="NZ GST shown on supplier record (NZD)"
              type="number"
              min="0"
              step="0.01"
              value={form.gst}
              onChange={change("gst")}
              required
            />
            <TextField
              label="Business use (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.business_percent}
              onChange={change("business_percent")}
              required
            />
            <TextField
              label="Fully paid date (leave blank if unpaid)"
              type="date"
              max={nzToday()}
              value={form.paid_date}
              onChange={change("paid_date")}
            />
          </div>
          <p className="text-sm text-zinc-500">
            Enter GST from the supplier record; overseas software and bank fees
            may have no NZ GST. Business use reduces the claim. Partial expense
            payments need separate accountant review.
          </p>
          <label className="flex gap-2 items-center text-sm">
            <input
              type="checkbox"
              checked={form.evidence_held}
              onChange={(e) =>
                setForm((f) => ({ ...f, evidence_held: e.target.checked }))
              }
            />
            I hold the supplier records supporting this expense and GST claim.
          </label>
          <div className="flex gap-2">
            <Button disabled={busy}>Save expense</Button>
            <Button
              variant="outline"
              type="button"
              disabled={busy}
              onClick={() => setForm(null)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error && <p role="alert">{error}</p>}
      {!data && !error && <p role="status">Loading expenses…</p>}
      {data?.items.length === 0 && (
        <div className="hard-card p-8 text-center">
          No expenses recorded yet.
        </div>
      )}
      {data?.items.map((exp) => (
        <article key={exp.id} className="hard-card p-4 space-y-3">
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <h3 className="font-bold">
                {exp.supplier} · {exp.reference}
              </h3>
              <p className="text-sm text-zinc-500">
                {exp.category} · {exp.date} ·{" "}
                {exp.voided_at
                  ? "Voided"
                  : exp.paid_date
                    ? `Paid ${exp.paid_date}`
                    : "Unpaid"}
              </p>
              <p className="text-sm">{exp.description}</p>
            </div>
            <div className="text-right">
              <strong>{money(exp.total_cents)}</strong>
              <p className="text-sm">
                Claimable GST {money(exp.claimable_gst_cents)} ·{" "}
                {exp.business_percent}% business
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            {!exp.voided_at && (
              <>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setEditing(exp);
                    setForm(
                      Object.fromEntries(
                        Object.keys(emptyExpense()).map((key) => [
                          key,
                          exp[key] ?? emptyExpense()[key],
                        ]),
                      ),
                    );
                  }}
                >
                  Correct expense
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    const reason = window.prompt(
                      "Reason for voiding this expense? This changes earlier worksheets; tell your accountant if already filed.",
                    );
                    if (reason)
                      run(async () => {
                        await api.post(
                          `${ROOT}/expenses/${exp.id}/void`,
                          { reason },
                          { params: { version: exp.version } },
                        );
                        toast.success(
                          "Expense voided; regenerate affected worksheets",
                        );
                      });
                  }}
                >
                  Void expense
                </Button>
              </>
            )}
            {!exp.paid_date && !exp.voided_at && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setPayment(exp)}
              >
                Record full payment
              </Button>
            )}
            {exp.receipt && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run(() =>
                    download(
                      `/expenses/${exp.id}/receipt`,
                      `receipt-${exp.reference.replace(/[^a-z0-9-]/gi, "_")}.${exp.receipt.ext}`,
                    ),
                  )
                }
              >
                Download receipt
              </Button>
            )}
            <label className="text-sm">
              {exp.receipt ? "Replace receipt" : "Attach receipt"} (PDF / PNG /
              JPG, max 5 MB)
              <input
                aria-label={`Receipt for ${exp.supplier} ${exp.reference}`}
                className="block mt-1 max-w-full"
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                disabled={busy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) {
                    toast.error("Receipt must be at most 5 MB");
                    return;
                  }
                  run(async () => {
                    const body = new FormData();
                    body.append("file", file);
                    await api.post(`${ROOT}/expenses/${exp.id}/receipt`, body);
                    toast.success("Receipt saved");
                  });
                }}
              />
            </label>
          </div>
        </article>
      ))}
      <Pages data={data} onPage={setPage} />
      {payment && (
        <PaymentForm
          key={payment.id}
          title={`Pay expense: ${payment.supplier}`}
          total={payment.total_cents}
          fullOnly
          busy={busy}
          onClose={() => setPayment(null)}
          onSave={(body) =>
            run(async () => {
              await api.post(`${ROOT}/expenses/${payment.id}/paid`, body);
              setPayment(null);
              toast.success("Expense payment recorded");
            })
          }
        />
      )}
    </div>
  );
}

function Reports({ config, busy, run }) {
  const [range, setRange] = useState({
    start: nzToday().slice(0, 7) + "-01",
    end: nzToday(),
    basis: config?.gst_basis || "",
  });
  const [report, setReport] = useState(null);
  const change = (key) => (value) => {
    setRange((r) => ({ ...r, [key]: value }));
    setReport(null);
  };
  return (
    <div className="space-y-4">
      <form
        className="hard-card p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          run(async () => {
            const { data } = await api.get(ROOT + "/report", { params: range });
            setReport(data);
          });
        }}
      >
        <h3 className="font-bold text-lg">GST worksheet</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <TextField
            label="Period start"
            type="date"
            required
            value={range.start}
            onChange={change("start")}
          />
          <TextField
            label="Period end"
            type="date"
            required
            min={range.start}
            value={range.end}
            onChange={change("end")}
          />
          <Field label="Accounting basis">
            <select
              className={fieldClass}
              required
              value={range.basis}
              onChange={(e) => change("basis")(e.target.value)}
            >
              <option value="">Choose basis</option>
              <option value="payments">Payments</option>
              <option value="invoice">Invoice</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy}>Calculate worksheet</Button>
          <Button
            type="button"
            variant="outline"
            disabled={
              busy || !range.start || !range.end || range.end < range.start
            }
            onClick={() =>
              run(() =>
                download(
                  "/ledger",
                  `finance-ledger-${range.start}-${range.end}.csv`,
                  { start: range.start, end: range.end },
                ),
              )
            }
          >
            Export invoices, expenses & payments
          </Button>
        </div>
        <p className="text-sm text-zinc-500">
          Choose the exact dates of your GST period. Payments basis uses dated
          payments; invoice basis uses issued invoices and supplier records
          received; hybrid uses invoice sales and paid expenses. Amounts reflect
          business use.
        </p>
      </form>
      {report && (
        <>
          <div className="p-4 border border-amber-400 bg-amber-50 text-amber-950 text-sm">
            <strong>For accountant review</strong>
            <p>{report.scope}</p>
            <p className="mt-2">
              Excluded: {report.legacy_invoice_count} legacy records;{" "}
              {report.stripe_org_count} Stripe-linked organisations.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              ["GST on sales", report.output_gst_cents],
              ["GST on expenses", report.input_gst_cents],
              [
                report.net_gst_cents < 0
                  ? "GST refund estimate"
                  : "Net GST estimate",
                Math.abs(report.net_gst_cents),
              ],
            ].map(([label, value]) => (
              <div className="hard-card p-5" key={label}>
                <p className="text-sm text-zinc-500">{label}</p>
                <strong className="text-2xl">{money(value)}</strong>
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() =>
              run(() =>
                download(
                  "/export",
                  `finance-${range.start}-${range.end}.csv`,
                  range,
                ),
              )
            }
          >
            Download accountant CSV
          </Button>
          <div className="hard-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {[
                    "Date",
                    "Type",
                    "Reference",
                    "Customer / supplier",
                    "Gross",
                    "GST",
                  ].map((h) => (
                    <th className="text-left p-3" key={h}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr className="border-t" key={i}>
                    <td className="p-3">{row.date}</td>
                    <td className="p-3">{row.kind}</td>
                    <td className="p-3">{row.number}</td>
                    <td className="p-3">{row.party}</td>
                    <td className="p-3">{money(row.gross_cents)}</td>
                    <td className="p-3">{money(row.gst_cents)}</td>
                  </tr>
                ))}
                {!report.rows.length && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center">
                      No reportable entries in this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="text-sm text-zinc-500">
        <a
          className="underline"
          href="https://www.ird.govt.nz/gst/filing-and-paying-gst-and-refunds/calculating-gst"
          target="_blank"
          rel="noreferrer"
        >
          IRD: calculating GST
        </a>{" "}
        ·{" "}
        <a
          className="underline"
          href="https://www.ird.govt.nz/gst/tax-invoices-for-gst"
          target="_blank"
          rel="noreferrer"
        >
          Taxable supply records
        </a>
      </p>
    </div>
  );
}
